import React, { useEffect, useState } from "react";
import axios from "axios";
import BarberSubsMPSubsBlock from "./BarberSubsMPSubsBlock";
import BarberSubsMPManageBlock from "./BarberSubsMPManageBlock";
import { MyDomain } from "../../App";
import BarberSubsHistory from "./BarberSubsHistory";

export default function BarberSubsMainPage() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [activeSub, setActiveSub] = useState(null);
  const [cancelledSub, setCancelledSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barberInfo, setBarberInfo] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const barberRaw = localStorage.getItem("barber");
    if (barberRaw) {
      try {
        setBarberInfo(JSON.parse(barberRaw));
      } catch {}
    }
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    setLoading(true);
    try {
      const barberRaw = localStorage.getItem("barber");
      const barber = barberRaw ? JSON.parse(barberRaw) : null;

      if (!barber?.phoneNumber) {
        setLoading(false);
        return;
      }

      const response = await axios.post(`${MyDomain}Subscription/GetAllSubs`, {
        phoneNumber: barber.phoneNumber,
      });

      const subs = response.data;

      if (!subs || subs.length === 0) {
        setIsSubscribed(false);
        setActiveSub(null);
        setCancelledSub(null);
        setLoading(false);
        return;
      }

      const latestSub = subs.reduce((prev, curr) =>
        curr.id > prev.id ? curr : prev,
      );

      const dateStillValid = isDateEndValid(latestSub.dateEnd);
      const isCancelled = latestSub.status === 2;

      if (isCancelled) {
        setIsSubscribed(false);
        setActiveSub(null);
        setCancelledSub(dateStillValid ? latestSub : null);
      } else if (dateStillValid) {
        setIsSubscribed(true);
        setActiveSub(latestSub);
        setCancelledSub(null);
      } else {
        setIsSubscribed(false);
        setActiveSub(null);
        setCancelledSub(null);
      }
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setIsSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  const isDateEndValid = (dateStr) => {
    if (!dateStr) return false;
    const [day, month, year] = dateStr.split("-").map(Number);
    const dateEnd = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateEnd >= today;
  };

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.loadingInner}>
          <span style={styles.loadingIcon}>✦</span>
          <p style={styles.loadingText}>Checking subscription…</p>
        </div>
        <style>{fonts}</style>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <style>{fonts}</style>

      {/* ── Barber info bar ── */}
      {barberInfo && (
        <div style={styles.infoBar}>
          <div style={styles.avatarCircle}>
            {barberInfo.name ? barberInfo.name?.charAt(0).toUpperCase() : "B"}
          </div>
          <div style={styles.infoBarText}>
            <p style={styles.infoBarName}>{barberInfo.name ?? "—"}</p>
            <p style={styles.infoBarPhone}>
              {barberInfo.countryNumber ?? ""} {barberInfo.phoneNumber ?? ""}
            </p>
          </div>
          <button
            style={{
              ...styles.historyBtn,
              ...(showHistory ? styles.historyBtnActive : {}),
            }}
            onClick={() => setShowHistory((v) => !v)}
          >
            {showHistory ? "← Back" : "History"}
          </button>
        </div>
      )}

      {showHistory ? (
        <BarberSubsHistory />
      ) : isSubscribed ? (
        <BarberSubsMPManageBlock
          activeSub={activeSub}
          onRecheck={checkSubscription}
        />
      ) : (
        <BarberSubsMPSubsBlock
          onSuccess={checkSubscription}
          cancelledSub={cancelledSub}
        />
      )}
    </div>
  );
}

const fonts = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@300;400&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #0f0d0a; }
`;

const styles = {
  root: {
    background: "#0f0d0a",
    minHeight: "100vh",
  },

  // ── Barber info bar ──────────────────────────────────────────────────────
  // Normal flow — no absolute positioning — works on all screen sizes
  infoBar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 20px",
    borderBottom: "1px solid #1e1a14",
    background: "#0f0d0a",
    // prevent text overflow on very small screens
    minWidth: 0,
    overflow: "hidden",
  },
  avatarCircle: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #c8a96e, #a08040)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 700,
    color: "#0f0d0a",
    fontFamily: "'DM Mono', monospace",
    flexShrink: 0,
  },
  infoBarText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0, // allows ellipsis to work on children
    flex: 1,
  },
  infoBarName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "15px",
    fontWeight: 600,
    color: "#e8ddd0",
    letterSpacing: "0.04em",
    lineHeight: 1.2,
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  infoBarPhone: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "10px",
    color: "#5a5048",
    letterSpacing: "0.06em",
    lineHeight: 1.2,
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  historyBtn: {
    marginLeft: "auto",
    flexShrink: 0,
    background: "transparent",
    border: "1px solid #2a2520",
    borderRadius: "20px",
    color: "#7a6a5a",
    padding: "5px 14px",
    fontFamily: "'DM Mono', monospace",
    fontSize: "11px",
    letterSpacing: "0.1em",
    cursor: "pointer",
  },
  historyBtnActive: {
    borderColor: "#c8a96e",
    color: "#c8a96e",
  },

  // ── Loading ──────────────────────────────────────────────────────────────
  loadingWrap: {
    minHeight: "100vh",
    background: "#0f0d0a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loadingInner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
  },
  loadingIcon: {
    color: "#c8a96e",
    fontSize: "28px",
  },
  loadingText: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "12px",
    letterSpacing: "0.2em",
    color: "#5a5048",
  },
};
